import { Controller } from 'stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import Meta from '@/js/util/meta';

const TIMEOUT = 5000;

// Marks the row as read before its link runs, so the page it opens already counts it as read.
// The click is replayed once the request settles, letting Turbo, the discussion panel and the
// sheet's close action handle it as before.
export default class extends Controller {
  static values = { url: String, ids: Array };

  #stream = null;
  #holding = false;
  #released = false;
  #rendered = false;

  async mark(event) {
    if (this.#released) {
      return;
    }

    this.#stream ??= this.#send();

    if (this.#opensElsewhere(event)) {
      const stream = await this.#stream;
      // A held click renders after its replay, so the link is still in the page when clicked.
      if (!this.#holding) {
        this.#render(stream);
      }
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#holding) {
      return;
    }

    this.#holding = true;
    const link = event.currentTarget;
    const stream = await this.#stream;
    this.#released = true;
    link.click();
    this.#render(stream);
  }

  #opensElsewhere(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  async #send() {
    const body = new FormData();
    this.idsValue.forEach((id) => body.append('ids[]', id));

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        body,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': Meta.get('csrf-token'),
        },
        signal: AbortSignal.timeout(TIMEOUT),
      });

      // An expired session redirects to the sign in page, which is not a stream.
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || response.redirected || !contentType.includes('turbo-stream')) {
        return null;
      }

      return await response.text();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  #render(stream) {
    if (stream && !this.#rendered) {
      this.#rendered = true;
      Turbo.renderStreamMessage(stream);
    }
  }
}
